/** 背景推播的平台教學：從瀏覽器的 User-Agent 猜是哪種裝置，挑出對應的設定步驟。
 *
 *  - 只用來決定預設分頁與提醒，猜錯時使用者可以自己切換平台。
 *  - iPad 開「桌面版網站」時 UA 和 Mac 一樣，要看觸控點數才分得出來。
 *  - iOS 26 的 Safari 把 UA 裡的系統版本固定寫成 18_6，實際版本要看 Version/26.x。
 */

export type GuidePlatform = 'ios' | 'android' | 'mac' | 'windows';
export const GUIDE_PLATFORMS: GuidePlatform[] = ['ios', 'android', 'mac', 'windows'];

export type DeviceInfo = {
  platform: GuidePlatform | 'other';
  tablet: boolean;
  browser: 'safari' | 'chrome' | 'edge' | 'firefox' | 'samsung' | 'other';
  /** 在 LINE、Facebook 這類 App 內建的瀏覽器開啟時，記下 App 名稱 */
  inApp: string | null;
  iosVersion: string | null;
  /** iOS 16.4 以前沒有網頁推播 */
  iosTooOld: boolean;
};

const IN_APP: [RegExp, string][] = [
  [/\bLine\//i, 'LINE'],
  [/FBAN|FBAV|FB_IAB/, 'Facebook'],
  [/Instagram/, 'Instagram'],
  [/MicroMessenger/, 'WeChat'],
];

function detectPlatform(ua: string, touchPoints: number): Pick<DeviceInfo, 'platform' | 'tablet'> {
  if (/iPhone|iPod/.test(ua)) return { platform: 'ios', tablet: false };
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1)) return { platform: 'ios', tablet: true };
  if (/Android/.test(ua)) return { platform: 'android', tablet: !/Mobile/.test(ua) };
  if (/Windows NT/.test(ua)) return { platform: 'windows', tablet: false };
  if (/Macintosh|Mac OS X/.test(ua)) return { platform: 'mac', tablet: false };
  return { platform: 'other', tablet: false };
}

function detectBrowser(ua: string, platform: DeviceInfo['platform']): DeviceInfo['browser'] {
  // 順序有意義：Edge、Samsung 的 UA 也帶 Chrome/，iOS 上每個瀏覽器的 UA 都帶 Safari/
  if (/EdgiOS|EdgA\/|Edg\//.test(ua)) return 'edge';
  if (/SamsungBrowser/.test(ua)) return 'samsung';
  if (/FxiOS|Firefox\//.test(ua)) return 'firefox';
  if (/OPR\/|OPiOS/.test(ua)) return 'other';
  if (/CriOS|Chrome\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua) && (platform === 'ios' || platform === 'mac')) return 'safari';
  return 'other';
}

/** 取「OS 18_6」與「Version/26.0」兩者較新的那個 */
function detectIosVersion(ua: string): [number, number] | null {
  const found = [/OS (\d+)[_.](\d+)/, /Version\/(\d+)\.(\d+)/]
    .map((pattern) => ua.match(pattern))
    .filter((match): match is RegExpMatchArray => match !== null)
    .map((match): [number, number] => [Number(match[1]), Number(match[2])]);
  if (!found.length) return null;
  return found.reduce((a, b) => (b[0] > a[0] || (b[0] === a[0] && b[1] > a[1]) ? b : a));
}

export function detectDevice(ua: string, maxTouchPoints = 0): DeviceInfo {
  const { platform, tablet } = detectPlatform(ua, maxTouchPoints);
  const version = platform === 'ios' ? detectIosVersion(ua) : null;
  return {
    platform,
    tablet,
    browser: detectBrowser(ua, platform),
    inApp: IN_APP.find(([pattern]) => pattern.test(ua))?.[1] ?? null,
    iosVersion: version ? `${version[0]}.${version[1]}` : null,
    iosTooOld: version !== null && (version[0] < 16 || (version[0] === 16 && version[1] < 4)),
  };
}

const PLATFORM_NAME = (info: DeviceInfo) => ({
  ios: info.tablet ? 'iPad' : 'iPhone',
  android: info.tablet ? 'Android 平板' : 'Android 手機',
  mac: 'Mac',
  windows: 'Windows',
  other: '',
})[info.platform];

const BROWSER_NAME: Record<DeviceInfo['browser'], string> = {
  safari: 'Safari', chrome: 'Chrome', edge: 'Edge', firefox: 'Firefox', samsung: 'Samsung Internet', other: '其他瀏覽器',
};

/** 「iPhone・Safari」；認不出平台時回傳 null */
export function describeDevice(info: DeviceInfo): string | null {
  if (info.platform === 'other') return null;
  return `${PLATFORM_NAME(info)}・${info.inApp ? `${info.inApp} 內建瀏覽器` : BROWSER_NAME[info.browser]}`;
}

/** 認不出的桌機（例如 Linux）用 Windows 的步驟，同樣是 Chrome、Edge 的設定 */
export function guideFor(info: DeviceInfo): GuidePlatform {
  return info.platform === 'other' ? 'windows' : info.platform;
}

/** 照步驟做之前就該先知道的事 */
export function deviceNotices(info: DeviceInfo): string[] {
  const notices: string[] = [];
  if (info.inApp) {
    const browser = info.platform === 'ios' ? 'Safari' : info.platform === 'android' ? 'Chrome' : '一般瀏覽器';
    notices.push(`目前是在 ${info.inApp} 內建的瀏覽器開啟，收不到背景推播。請從選單改用 ${browser} 開啟這個網站。`);
  }
  if (info.iosTooOld) {
    notices.push(`這台裝置的 iOS 是 ${info.iosVersion}，要更新到 16.4 以上才收得到背景推播。`);
  }
  return notices;
}

export type PushGuideContent = {
  tab: string;
  /** 直接回答「這個平台收不收得到背景推播」 */
  answer: string;
  steps: string[];
  notes: string[];
};

const ENABLE_STEP = '點右上角的通知鈴鐺，切到「通知設定」，按「開啟背景推播」，詢問通知權限時選「允許」。';
const UNBLOCK_NOTE = '之前按過封鎖的話，點網址列左側的圖示進入網站設定，把「通知」改成允許。';

export const PUSH_GUIDES: Record<GuidePlatform, PushGuideContent> = {
  ios: {
    tab: 'iPhone',
    answer: 'iPhone、iPad 收得到，但要 iOS 16.4 以上，而且要把平台加入主畫面、從主畫面的圖示開啟。只在 Safari 分頁開著平台，收不到背景推播。',
    steps: [
      '用 Safari 開啟這個網站。從 LINE 等 App 點開的，先改用 Safari 開啟。',
      '點「分享」，往下找到「加入主畫面」，再點「加入」。iOS 26 的「分享」收在網址列旁的「⋯」裡；畫面上有網頁 App 的開關時，請保持開啟。',
      '回到主畫面，點剛加入的「EdgeAI」圖示開啟，再登入一次。主畫面的平台和 Safari 是分開登入的。',
      ENABLE_STEP,
      '按「送測試通知」後馬上鎖定螢幕，約 5 秒內應該會跳出通知。',
    ],
    notes: [
      '到「設定」→「通知」→「EdgeAI」，確認「允許通知」是開啟的。',
      '專注模式開啟時，通知不會跳出，只會留在通知中心。',
    ],
  },
  android: {
    tab: 'Android',
    answer: 'Android 收得到，用 Chrome 開啟就可以，不必加入主畫面。手機的省電設定可能讓通知晚一點到。',
    steps: [
      '用 Chrome 開啟這個網站。從 LINE 等 App 點開的，先改用 Chrome 開啟。',
      ENABLE_STEP,
      '到手機的「設定」→「應用程式」→「Chrome」→「通知」，確認通知是開啟的。',
      '按「送測試通知」後回到主畫面或關掉螢幕，約 5 秒內應該會跳出通知。',
    ],
    notes: [
      UNBLOCK_NOTE,
      '通知常常晚到的話，把 Chrome 的電池用量改成「不受限制」；各品牌手機的選項名稱可能不同。',
    ],
  },
  mac: {
    tab: 'Mac',
    answer: 'Mac 收得到。Safari（macOS 13 以上）、Chrome、Edge 都支援；用 Chrome 或 Edge 時，瀏覽器要保持執行。',
    steps: [
      '用 Safari、Chrome 或 Edge 開啟這個網站。',
      ENABLE_STEP,
      '到「系統設定」→「通知」，找到你用的瀏覽器，打開「允許通知」。',
      '按「送測試通知」後切到其他 App，約 5 秒內螢幕右上角應該會跳出通知。',
    ],
    notes: [
      '用 Chrome 或 Edge 時可以關掉視窗，但不要按 ⌘Q 結束程式，結束後就收不到。',
      '之前按過封鎖的話：Safari 到「Safari」→「設定」→「網站」→「通知」改成允許；Chrome、Edge 點網址列左側的圖示改成允許。',
      '專注模式開啟時，通知不會跳出，只會留在通知中心。',
    ],
  },
  windows: {
    tab: 'Windows',
    answer: 'Windows 收得到。用 Chrome 或 Edge 開啟，並讓瀏覽器在關掉視窗後繼續在背景執行。',
    steps: [
      '用 Chrome 或 Edge 開啟這個網站。',
      ENABLE_STEP,
      '到 Windows 的「設定」→「系統」→「通知」，確認通知是開啟的，清單裡的 Chrome 或 Edge 也要開啟。',
      'Chrome 到「設定」→「系統」，Edge 到「設定」→「系統與效能」，打開讓瀏覽器關閉後仍在背景執行的選項（各版本的名稱略有不同）。',
      '按「送測試通知」後把瀏覽器縮到最小，約 5 秒內螢幕右下角應該會跳出通知。',
    ],
    notes: [
      '「請勿打擾」開啟時，通知不會跳出，只會收進通知中心。',
      UNBLOCK_NOTE,
    ],
  },
};
